import { FC } from 'react';
import { Box, MenuItem, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import { ON_PRIMARY_OUTLINE_FIELD_SX } from '../../components/fieldStyles';
import { colors } from '../../theme/tokens';
import {
  DATE_OPERATORS,
  DATE_OPERATOR_LABELS,
  DateCriterion,
  DateOperator,
  isDatePartial,
} from './criteria';

// A date criterion of the Recherche modal (XD screen 6): the
// operator dropdown ("Avant", "Après", "Entre") followed by the
// `JJ/MM/AAAA` date field, or by two when it is "Entre" (the XD draws the range as
// a single "JJ/MM/AAAA - JJ/MM/AAAA" field; two fields with a calendar are what
// DatePicker offers and what the backend receives, `_from` and `_to`).
//
// A half-filled criterion does not count (see criteria.isDateComplete); it is
// warned about in small print so that it is understood why the counter does not move.

type Props = {
  /** Prefix of the ids: `search-issued` → `search-issued-op`, `-from`, `-to`. */
  idPrefix: string;
  /** Accessible name of the group ("Emission"). */
  label: string;
  /** `id` of the row label, for `aria-labelledby`. */
  labelledBy?: string;
  value: DateCriterion;
  onChange: (value: DateCriterion) => void;
  error?: string;
};

const OPERATOR_WIDTH = 124;

const DateCriterionFields: FC<Props> = ({
  idPrefix,
  label,
  labelledBy,
  value,
  onChange,
  error,
}) => {
  const partial = !error && isDatePartial(value);

  const pickerProps = (id: string, placeholder: string) => ({
    textField: {
      id,
      size: 'small' as const,
      fullWidth: true,
      placeholder,
      error: Boolean(error),
      sx: ON_PRIMARY_OUTLINE_FIELD_SX,
    },
    field: { clearable: true },
  });

  return (
    <Box
      role="group"
      aria-labelledby={labelledBy}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          // Mobile (smartphone XD, S7): the operator sits full-width ABOVE
          // the date field, stacked, not beside it.
          [theme.breakpoints.down('tablet')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        })}
      >
        <Select
          id={`${idPrefix}-op`}
          size="small"
          value={value.op}
          onChange={(e) =>
            onChange({
              ...value,
              op: e.target.value as DateOperator,
              // "Entre" is the only one with an end date.
              to: e.target.value === 'between' ? value.to : null,
            })
          }
          inputProps={{ 'aria-label': `Opérateur ${label}` }}
          data-testid={`${idPrefix}-op`}
          sx={(theme) => ({
            ...(ON_PRIMARY_OUTLINE_FIELD_SX as object),
            width: OPERATOR_WIDTH,
            flexShrink: 0,
            [theme.breakpoints.down('tablet')]: { width: '100%' },
          })}
        >
          {DATE_OPERATORS.map((op) => (
            <MenuItem key={op} value={op}>
              {DATE_OPERATOR_LABELS[op]}
            </MenuItem>
          ))}
        </Select>

        <DatePicker
          value={value.from}
          onChange={(date: Dayjs | null) => onChange({ ...value, from: date })}
          slotProps={pickerProps(`${idPrefix}-from`, 'JJ/MM/AAAA')}
          sx={{ flex: 1, minWidth: 150 }}
        />

        {value.op === 'between' && (
          <>
            <Typography
              component="span"
              aria-hidden
              sx={(theme) => ({
                color: colors.white,
                // Stacked on mobile, the dash would be alone on a line.
                [theme.breakpoints.down('tablet')]: { display: 'none' },
              })}
            >
              -
            </Typography>
            <DatePicker
              value={value.to}
              onChange={(date: Dayjs | null) => onChange({ ...value, to: date })}
              slotProps={pickerProps(`${idPrefix}-to`, 'JJ/MM/AAAA')}
              sx={{ flex: 1, minWidth: 150 }}
            />
          </>
        )}
      </Box>

      {partial && (
        <Typography
          sx={{ fontSize: '0.75rem', color: colors.white, opacity: 0.75 }}
        >
          {value.op === 'between'
            ? 'Critère incomplet : saisissez les deux dates.'
            : 'Critère incomplet : saisissez une date valide.'}
        </Typography>
      )}
    </Box>
  );
};

export default DateCriterionFields;
